// ownerContactMembers — the Owner Contact form's entity MEMBER rows (the "other
// members or trustees" behind an LLC / trust), spec §14. PURE: type-only imports,
// so it tests under a bare `npx tsx --test src/lib/ownerContactMembers.test.ts`.
//
// §14 (Miles, 2026-09-25): the entity is ONE AppFolio owner. A member who should
// see statements and financials in the owner portal gets their EMAIL added to it —
// portal access follows the email — so the question is only asked once the member
// has one. Owner-facing strings: // Copy approved by Miles 2026-09-25.

import type { OwnerContactMemberContact } from './ownerContactClient';

/** A usable email — the thing that actually grants portal access. */
export function memberHasEmail(m: Pick<OwnerContactMemberContact, 'email'>): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((m.email ?? '').trim());
}

/**
 * Show the checkbox? With a usable email, yes. Without one, only while it is still
 * TICKED: a saved "yes" with no email counts as no access on the server (the
 * addendum records "no"; lead 8c68cb67), so the box stays in view — the owner can
 * see the stored answer and untick it — rather than hiding a "yes" that does nothing.
 */
export function showPortalAccessCheckbox(
  m: Pick<OwnerContactMemberContact, 'email' | 'portalAccess'>,
): boolean {
  return memberHasEmail(m) || m.portalAccess === true;
}

/** Ticked with no usable email yet — the hint (a plain hint, not an error) says how to make it count. */
export function portalAccessNeedsEmail(
  m: Pick<OwnerContactMemberContact, 'email' | 'portalAccess'>,
): boolean {
  return m.portalAccess === true && !memberHasEmail(m);
}

export function portalAccessLabel(m: Pick<OwnerContactMemberContact, 'firstName'>): string {
  const first = (m.firstName ?? '').trim();
  return `Give ${first || 'them'} access to the owner portal (statements and financials)`;
}

export const PORTAL_ACCESS_EMAIL_HINT = 'Add an email to give them owner portal access';
